import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ResetPassword } from '../../../shared/models/rest-password.model';
import { PasswordRecoveryRepository } from '../repositories/password-recovery.repository';

@Injectable({ providedIn: 'root' })
export class PasswordRecoveryService {
  constructor(private readonly passwordRepository: PasswordRecoveryRepository) {}

  /**
   * Strongly typed invoker to avoid dynamic `any` and `apply`.
   */
  private invoke<K extends keyof PasswordRecoveryRepository>(
    method: K,
    ...args: Parameters<PasswordRecoveryRepository[K]>
  ): ReturnType<PasswordRecoveryRepository[K]> {
    const fn = this.passwordRepository[method] as (...a: any[]) => any;
    return fn(...args);
  }

  /**
   * Initiates password recovery for a username.
   */
  recoverPassword(username: string): Observable<unknown> {
    const u = (username ?? '').trim();
    if (!u) {
      return throwError(() => new Error('Username is required'));
    }
    return this.invoke('recoverPassword', u).pipe(
      catchError((err) => throwError(() => new Error(`recoverPassword failed: ${String(err)}`))),
    );
  }

  /**
   * Resets password given username, reset token, and password payload.
   */
  resetPassword(username: string, resetToken: string, passwords: ResetPassword): Observable<unknown> {
    const u = (username ?? '').trim();
    const t = (resetToken ?? '').trim();

    if (!u || !t) {
      return throwError(() => new Error('Username and resetToken are required'));
    }
    if (!passwords || !passwords.password1 || !passwords.password2) {
      return throwError(() => new Error('passwords must include password1 and password2'));
    }

    return this.invoke('resetPassword', u, t, passwords).pipe(
      catchError((err) => throwError(() => new Error(`resetPassword failed: ${String(err)}`))),
    );
  }

  /**
   * Validates the reset token for a username.
   */
  checkResetTokenValid(username: string, token: string): Observable<unknown> {
    const u = (username ?? '').trim();
    const t = (token ?? '').trim();

    if (!u || !t) {
      return throwError(() => new Error('Username and token are required'));
    }

    return this.invoke('checkResetTokenValid', u, t).pipe(
      catchError((err) => throwError(() => new Error(`checkResetTokenValid failed: ${String(err)}`))),
    );
  }
}
